/**
 * The ONLY module allowed to read Zod internals.
 *
 * Log redaction (`safeZodIssues`), the MCP unknown-argument guard, the MCP
 * publish path, the MCP tools reference generator and several contract tests
 * all need to look inside schemas. They each used to walk internals by hand,
 * which is how a Zod upgrade breaks them silently. Everything goes through
 * here instead, pinned by `__tests__/introspect.contract.test.ts`;
 * `zod-internals-guard.test.ts` bans internals anywhere else. On a Zod
 * upgrade, only this file changes.
 *
 * Implementation: Zod 4.
 */
import { z } from 'zod';
/** Bounded so a self-referencing z.lazy cannot spin. */
const MAX_UNWRAP_DEPTH = 16;
/** One step inward through a structure-preserving wrapper, or the node itself. */
function unwrapOnce(node) {
    if (node instanceof z.ZodOptional ||
        node instanceof z.ZodNullable ||
        node instanceof z.ZodDefault ||
        node instanceof z.ZodPrefault ||
        node instanceof z.ZodCatch ||
        node instanceof z.ZodReadonly ||
        node instanceof z.ZodNonOptional ||
        node instanceof z.ZodLazy) {
        return node.unwrap();
    }
    if (node instanceof z.ZodPipe) {
        // transform: ZodPipe(input, ZodTransform) -> the input carries the shape.
        // preprocess: ZodPipe(ZodTransform, target) -> the target carries it.
        return (node.in instanceof z.ZodTransform ? node.out : node.in);
    }
    return node;
}
/**
 * Peel wrappers that do not change the value's structure: optional,
 * nullable, default, prefault, catch, readonly, nonoptional, lazy,
 * transforms, preprocess and pipelines (input side). Refinements and
 * `.brand()` add no wrapper in Zod 4, so there is nothing to peel for them.
 */
export function unwrapTransparent(schema) {
    let node = schema;
    for (let depth = 0; depth < MAX_UNWRAP_DEPTH; depth++) {
        const next = unwrapOnce(node);
        if (next === node)
            return node;
        node = next;
    }
    return node;
}
/** `.refine`/`.superRefine` add a `custom` check to the schema they are called on. */
function hasCustomCheck(node) {
    return (node.def.checks ?? []).some((check) => check._zod.def.check === 'custom');
}
/**
 * True when the schema itself (through its wrappers, not its fields) carries a
 * `.refine`/`.superRefine`. `defineFacet` records which version schemas carry
 * one (`refinedVersions`, spec A15) so P4's snapshot can mark them as only
 * partly described; deriving from a refined object by `.pick`/`.omit`/
 * `.partial` needs no guard, because Zod 4.3+ throws on it itself. Built-in
 * checks such as `.min()` are checks too, but not `custom` ones, so they do
 * not count.
 */
export function hasRefinements(schema) {
    let node = schema;
    for (let depth = 0; depth < MAX_UNWRAP_DEPTH; depth++) {
        if (hasCustomCheck(node))
            return true;
        const next = unwrapOnce(node);
        if (next === node)
            return false;
        node = next;
    }
    return false;
}
export function objectShape(schema) {
    const node = unwrapTransparent(schema);
    return node instanceof z.ZodObject ? node.shape : null;
}
export function objectUnknownKeys(schema) {
    const node = unwrapTransparent(schema);
    if (!(node instanceof z.ZodObject))
        return null;
    const catchall = node.def.catchall;
    if (catchall === undefined)
        return 'strip';
    return catchall instanceof z.ZodNever ? 'strict' : 'passthrough';
}
/** `z.discriminatedUnion` is an instance of ZodUnion, so this covers both. */
export function unionOptions(schema) {
    const node = unwrapTransparent(schema);
    return node instanceof z.ZodUnion ? node.options : null;
}
export function intersectionSides(schema) {
    const node = unwrapTransparent(schema);
    return node instanceof z.ZodIntersection
        ? [node.def.left, node.def.right]
        : null;
}
export function arrayElement(schema) {
    const node = unwrapTransparent(schema);
    return node instanceof z.ZodArray ? node.element : null;
}
export function recordValue(schema) {
    const node = unwrapTransparent(schema);
    return node instanceof z.ZodRecord ? node.valueType : null;
}
export function enumValues(schema) {
    const node = unwrapTransparent(schema);
    return node instanceof z.ZodEnum ? node.options.map(String) : null;
}
export function literalValues(schema) {
    const node = unwrapTransparent(schema);
    return node instanceof z.ZodLiteral ? [...node.values] : null;
}
export function stringLengthBounds(schema) {
    const node = unwrapTransparent(schema);
    if (!(node instanceof z.ZodString))
        return null;
    return {
        min: node.minLength ?? undefined,
        max: node.maxLength ?? undefined,
    };
}
//# sourceMappingURL=introspect.js.map